import { convertAssetPathToUrl } from "../../config/assets";
import { NoticeType } from "../../types/notice";
import { buildEventRecurrencePayload, formatLocalDate } from "../events/eventRecurrence";
import { sanitizeDriveFolderMatchKey } from "../../utils/driveFolder";
import type {
  DraftRestorePayload,
  DraftRestoreSource,
  DraftRestoreUploadedFile,
} from "../../utils/draftRestore";

export type NoticeUploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type NoticeFormSnapshot = {
  title?: string;
  body?: string;
  type?: string;
  attachmentUrl?: string;
  uploadedFiles?: NoticeUploadedFile[];
  eventStartDate?: Date | null;
  eventStartTime?: string;
  eventEndDate?: Date | null;
  eventEndTime?: string;
  eventLocation?: string;
  eventBackgroundImage?: string;
  eventFontColor?: string;
  eventReminderMode?: string;
  eventReminderModes?: string[];
  targetSubcircle?: string;
  isUrgent?: boolean;
  pollOptions?: Array<{ optionId: string; label: string; order: number }>;
  pollClosesAt?: string;
  pollMode?: "standard" | "claim";
  rsvpEnabled?: boolean;
  rsvpClosesAt?: string;
  publishMode?: "now" | "schedule";
  publishAt?: string;
  staffOnly?: boolean;
  driveFolderKey?: string;
  eventRecurrenceFrequency?: string;
  eventRecurrenceUntil?: Date | null;
};

export type NoticePayloadContext = {
  defaultDriveFolderKey?: string;
  templateId?: string;
  targetUserId?: string;
  titleOverride?: string;
  uploadedFiles?: NoticeUploadedFile[];
};

export type NoticePayloadMode = "publish" | "draft";

export type NoticeWirePayload = {
  title: string;
  body: string;
  type: string;
  isUrgent: boolean;
  targetSubcircle?: string;
  targetUserId?: string;
  attachmentUrl?: string;
  uploadedFiles?: NoticeUploadedFile[];
  eventStartDate?: string;
  eventStartTime?: string;
  eventEndDate?: string;
  eventEndTime?: string;
  eventLocation?: string;
  eventBackgroundImage?: string;
  eventFontColor?: string;
  eventReminderModes?: string[];
  eventReminderMode?: string;
  createdFromTemplate?: boolean;
  templateId?: string;
  pollOptions?: Array<{ optionId: string; label: string; order: number }>;
  pollClosesAt?: string;
  pollMode?: "claim";
  rsvpEnabled?: boolean;
  rsvpClosesAt?: string;
  staffOnly?: boolean;
  publishAt?: string;
  driveFolderKey?: string;
  eventRecurrence?: ReturnType<typeof buildEventRecurrencePayload>;
};

function isImageNoticeType(type: string | undefined): boolean {
  return type === "image" || type === "image_notice";
}

function isEventNoticeType(type: string | undefined): boolean {
  const t = (type || "").toLowerCase();
  return t === "event" || t === "custom_event";
}

/**
 * Normalize type for POST /v1/notices.
 * Do not use client.getValidNoticeType — that helper is a legacy chart
 * mapper (info|event|image only) and incorrectly coerces poll → info.
 */
export function normalizeNoticeTypeForPublish(
  type: string | undefined,
): Exclude<NoticeType, "urgent" | "image_notice"> | "info" | "image" {
  if (!type) return "info";
  const validType = type.toLowerCase();
  if (validType === "urgent") return "info";
  if (validType === "image_notice") return "image";
  if (
    validType === "info" ||
    validType === "event" ||
    validType === "image" ||
    validType === "custom_event" ||
    validType === "direct" ||
    validType === "poll"
  ) {
    return validType;
  }
  return "info";
}

export function resolveFormIsUrgent(form: {
  type?: string;
  isUrgent?: boolean;
  staffOnly?: boolean;
}): boolean {
  if (form.staffOnly === true) return false;
  if (form.isUrgent === true) return true;
  return (form.type || "").toLowerCase() === "urgent";
}

export function mapPollOptionsForWire(
  options: NoticeFormSnapshot["pollOptions"],
  mode: NoticePayloadMode,
): Array<{ optionId: string; label: string; order: number }> {
  return (options || [])
    .map((o, i) => ({
      optionId: o.optionId || `opt-${i}`,
      label: mode === "draft" ? (o.label || "").trim() : o.label.trim(),
      order: i,
    }))
    .filter((o) => o.label);
}

function mapPollClosesAt(
  pollClosesAt: string | undefined,
  mode: NoticePayloadMode,
): string | undefined {
  if (!pollClosesAt?.trim()) return undefined;
  if (mode === "draft") {
    const d = new Date(pollClosesAt);
    return Number.isFinite(d.getTime()) ? d.toISOString() : pollClosesAt.trim();
  }
  return new Date(pollClosesAt).toISOString();
}

function resolveDraftBody(form: NoticeFormSnapshot): string {
  const bodyValue = form.body || "";
  const body =
    isImageNoticeType(form.type) && bodyValue.trim() === ""
      ? ""
      : bodyValue.trim();
  return body || "";
}

function resolvePublishBody(form: NoticeFormSnapshot): string {
  return form.body || "";
}

function hasAttachments(form: NoticeFormSnapshot, files?: NoticeUploadedFile[]): boolean {
  return Boolean((files && files.length > 0) || form.attachmentUrl);
}

/**
 * The Drive folder a Notice's attachments are filed in: its Circle's, or the
 * school's for Everyone. Undefined when the Circle isn't known.
 */
export function noticeDriveFolderKey(
  targetSubcircle: string | undefined,
  circles: ReadonlyArray<{ circleId: string; name?: string }>,
  schoolName: string | undefined,
): string | undefined {
  const folder = targetSubcircle
    ? circles.find((circle) => circle.circleId === targetSubcircle)?.name
    : schoolName || "Everyone";
  return folder?.replace(/[/\\]/g, "-").trim() || undefined;
}

/** Form snapshot → POST /v1/notices (publish) or createDraft (draft). */
export function buildNoticePayload(
  form: NoticeFormSnapshot,
  ctx: NoticePayloadContext = {},
  mode: NoticePayloadMode = "publish",
): NoticeWirePayload {
  const uploadedFiles = ctx.uploadedFiles ?? form.uploadedFiles;
  const payload: NoticeWirePayload = {
    title: ((mode === "publish" ? ctx.titleOverride : undefined) || form.title || "").trim(),
    body: mode === "draft" ? resolveDraftBody(form) : resolvePublishBody(form),
    type:
      mode === "draft"
        ? form.type || "info"
        : normalizeNoticeTypeForPublish(form.type),
    isUrgent:
      mode === "draft" ? form.isUrgent || false : resolveFormIsUrgent(form),
    targetSubcircle: form.targetSubcircle || undefined,
    attachmentUrl: form.attachmentUrl || undefined,
    uploadedFiles:
      uploadedFiles && uploadedFiles.length > 0 ? uploadedFiles : undefined,
    eventStartDate: form.eventStartDate
      ? formatLocalDate(form.eventStartDate)
      : undefined,
    eventStartTime: form.eventStartTime || undefined,
    eventEndDate: form.eventEndDate
      ? formatLocalDate(form.eventEndDate)
      : undefined,
    eventEndTime: form.eventEndTime || undefined,
    eventLocation: form.eventLocation || undefined,
    eventBackgroundImage:
      form.eventBackgroundImage && form.eventBackgroundImage.trim() !== ""
        ? convertAssetPathToUrl(form.eventBackgroundImage.trim())
        : undefined,
    eventFontColor:
      form.eventFontColor && form.eventFontColor.trim() !== ""
        ? form.eventFontColor.trim()
        : undefined,
    createdFromTemplate: ctx.templateId ? true : undefined,
    templateId: ctx.templateId || undefined,
  };

  if (form.type === "poll") {
    payload.pollOptions = mapPollOptionsForWire(form.pollOptions, mode);
    payload.pollClosesAt = mapPollClosesAt(form.pollClosesAt, mode);
    if (form.pollMode === "claim") {
      payload.pollMode = "claim";
    }
  }

  if (mode === "publish") {
    if (ctx.targetUserId) {
      payload.targetUserId = ctx.targetUserId;
    }

    if ((form.type === "event" || form.eventStartDate) && form.staffOnly !== true) {
      payload.eventReminderModes = form.eventReminderModes ?? [];
      payload.eventReminderMode =
        form.eventReminderModes?.[0] || form.eventReminderMode || "off";
    }

    if ((form.type === "event" || form.eventStartDate) && form.rsvpEnabled) {
      payload.rsvpEnabled = true;
      payload.rsvpClosesAt = form.rsvpClosesAt?.trim()
        ? new Date(form.rsvpClosesAt).toISOString()
        : undefined;
    }

    if (isEventNoticeType(form.type) && form.staffOnly === true) {
      payload.staffOnly = true;
    }

    if (
      form.publishMode === "schedule" &&
      form.type !== "poll" &&
      form.type !== "direct" &&
      form.staffOnly !== true
    ) {
      payload.publishAt = new Date(form.publishAt || "").toISOString();
    }

    const driveFolderKey = form.driveFolderKey?.trim() || ctx.defaultDriveFolderKey?.trim();
    if (hasAttachments(form, uploadedFiles) && driveFolderKey) {
      payload.driveFolderKey = sanitizeDriveFolderMatchKey(
        driveFolderKey,
      );
    }

    if (
      isEventNoticeType(form.type) &&
      form.eventRecurrenceFrequency &&
      form.eventRecurrenceFrequency !== "none"
    ) {
      payload.eventRecurrence = buildEventRecurrencePayload(
        form.eventRecurrenceFrequency,
        form.eventRecurrenceUntil || null,
      );
    }
  }

  return payload;
}

export type NoticeRestoreMode = "duplicate" | "draft";

export type NoticeRestoreSource = DraftRestoreSource & {
  eventReminderMode?: string;
  eventReminderModes?: string[];
  targetUserId?: string;
  staffOnly?: boolean;
};

export type NoticeRestorePayload = {
  noticeType: NoticeType;
  flowId?: "claims";
  initialFormData: DraftRestorePayload["initialFormData"] & {
    eventReminderMode?: string;
    eventReminderModes?: string[];
    targetUserId?: string;
  };
};

const parseOptionalDate = (dateString?: string): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const noticeTypeFromDraftType = (type: string): NoticeType =>
  type === "image_notice" ? "image" : (type as NoticeType);

export const noticeTypeForDuplicate = (type: string): NoticeType => {
  const t = (type || "info").toLowerCase();
  if (t === "event" || t === "custom_event") return "custom_event";
  if (t === "image_notice" || t === "image") return "image";
  if (t === "poll") return "poll";
  if (t === "direct") return "direct";
  if (t === "urgent") return "urgent";
  return "info";
};

export const restoredUploadedFilesFromDraft = (
  draft: Pick<DraftRestoreSource, "uploadedFiles" | "attachments">,
): DraftRestoreUploadedFile[] => {
  if (Array.isArray(draft.uploadedFiles)) {
    return draft.uploadedFiles;
  }
  if (Array.isArray(draft.attachments)) {
    return draft.attachments.map((file) => ({
      fileUrl: file.url ?? "",
      fileName: file.name ?? "",
      contentType: file.type ?? "",
      size: file.size ?? 0,
    }));
  }
  return [];
};

/** Stored Notice → Create Notice form prefill. */
export function restoreNoticeForm(
  source: NoticeRestoreSource,
  mode: NoticeRestoreMode = "duplicate",
): NoticeRestorePayload {
  const clearDates = mode === "duplicate" && isEventNoticeType(source.type);
  const reminderModes = Array.isArray(source.eventReminderModes)
    ? source.eventReminderModes.filter((m) => typeof m === "string" && m.trim())
    : undefined;
  const isClaimPoll = source.type === "poll" && source.pollMode === "claim";

  return {
    noticeType:
      mode === "draft"
        ? noticeTypeFromDraftType(source.type)
        : noticeTypeForDuplicate(source.type),
    ...(mode === "duplicate" && isClaimPoll ? { flowId: "claims" as const } : {}),
    initialFormData: {
      title: source.title,
      body: source.body,
      eventBackgroundImage: source.eventBackgroundImage,
      eventFontColor: source.eventFontColor,
      targetSubcircle: source.targetSubcircle,
      isUrgent: source.isUrgent,
      eventStartDate: clearDates
        ? null
        : source.eventStartDate
          ? parseOptionalDate(source.eventStartDate)
          : null,
      eventStartTime: source.eventStartTime || "",
      eventEndDate: clearDates
        ? null
        : source.eventEndDate
          ? parseOptionalDate(source.eventEndDate)
          : null,
      eventEndTime: source.eventEndTime || "",
      eventLocation: source.eventLocation || "",
      audience: source.targetSubcircle ? "subcircle" : "circle",
      uploadedFiles: restoredUploadedFilesFromDraft(source),
      ...(mode === "draft" ? { draftId: source.noticeId } : {}),
      ...(mode === "duplicate" && reminderModes?.length
        ? {
            eventReminderModes: [...reminderModes],
            eventReminderMode: reminderModes[0],
          }
        : mode === "duplicate" && source.eventReminderMode
          ? {
              eventReminderMode: source.eventReminderMode,
              eventReminderModes:
                source.eventReminderMode === "off"
                  ? []
                  : [source.eventReminderMode],
            }
          : {}),
      ...(mode === "duplicate" && source.targetUserId
        ? { targetUserId: source.targetUserId }
        : {}),
      ...(Array.isArray(source.pollOptions) && source.pollOptions.length > 0
        ? {
            pollOptions: source.pollOptions.map((o, i) => ({
              optionId: o.optionId || `opt-${i}`,
              label: o.label || "",
              order: typeof o.order === "number" ? o.order : i,
            })),
          }
        : {}),
      ...(source.pollClosesAt?.trim()
        ? { pollClosesAt: source.pollClosesAt.trim() }
        : {}),
      ...(source.pollMode === "claim" ? { pollMode: "claim" as const } : {}),
    },
  };
}
